import type { Node } from "@xyflow/react"
import { AlertTriangle, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"
import type { ProjectNodeData } from "@/types/project"

type PaletteNodeActionsProps = {
	selectedNodes: Node<ProjectNodeData>[]
	onDeleteNodes: (nodeIds: string[]) => void
}

function ActionButton({
	className,
	onClick,
	children,
}: {
	className?: string
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"inline-flex items-center gap-1.5 rounded-md border border-border/50 px-2 py-1 text-xs text-foreground/90 transition-colors",
				"hover:bg-muted/70 focus:outline-none focus:ring-2 focus:ring-primary/35",
				className,
			)}
		>
			{children}
		</button>
	)
}

export function PaletteNodeActions({
	selectedNodes,
	onDeleteNodes,
}: PaletteNodeActionsProps) {
	if (selectedNodes.length === 0) return null

	const isSingleNode = selectedNodes.length === 1
	const primaryNode = isSingleNode ? selectedNodes[0] : undefined
	const nodeError =
		typeof primaryNode?.data?.error === "string"
			? primaryNode.data.error.trim()
			: ""

	return (
		<div className="space-y-2 border-t border-border/40 bg-muted/10 px-3 py-2">
			<div className="flex items-center justify-between gap-2">
				<div className="text-[11px] text-muted-foreground">
					{isSingleNode
						? (primaryNode?.data?.label ?? "Selected node")
						: `${selectedNodes.length} nodes selected`}
				</div>
				<div className="flex items-center gap-1.5">
					<ActionButton
						className="text-red-500 hover:bg-red-500/10"
						onClick={() => onDeleteNodes(selectedNodes.map((node) => node.id))}
					>
						<Trash2 className="h-3.5 w-3.5" />
						Delete
					</ActionButton>
				</div>
			</div>

			{nodeError ? (
				<div className="flex items-start gap-2 rounded-md border border-red-500/35 bg-red-500/10 px-2 py-1.5 text-xs text-red-500">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span className="line-clamp-2">{nodeError}</span>
				</div>
			) : null}
		</div>
	)
}
